import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, TimeoutError } from "rxjs";
import { map } from "rxjs/operators";
import { Messages } from "../../../helpers/messages";
import { PageBuilderMenuProvider } from "../../../pagebuilder/services/menu/repository";
import { FcmServiceProvider } from "../../../providers/fcm-service/fcm-service";
import { AccessManagementApiProvider } from "../../../shared/nestjs/access-management/access-management-api";
import { RequestNewForgotPasswordDto } from "../../../shared/nestjs/gd-only/dto/access-managements.dto";
import { AuthApi } from "../../../shared/nestjs/gd-only/services/auth.api";
import { NestApiProvider } from "../../../shared/nestjs/nest-api";
import { LoopBackAuth } from "../../../shared/sdk";
import { AccessmanagementApi } from "../../../shared/sdk/services/custom/Accessmanagement";
import { AuthenticationBloc } from "../../authentication/authentication.bloc";
import { InitializeBloc } from "../../authentication/initialize.bloc";

@Injectable()
export class ForgotPasswordBloc {

  constructor(
    private accessManagementNestApi: AccessManagementApiProvider,
    private accessmanagementApi: AccessmanagementApi,
    private nestApiProvider: NestApiProvider,
    private authenticationBloc: AuthenticationBloc,
    private authApi: AuthApi,
    private menuRepository: PageBuilderMenuProvider,
    private fcmServiceProvider: FcmServiceProvider,
    private initializeBloc: InitializeBloc,
    @Inject(LoopBackAuth) protected auth: LoopBackAuth,
  ) {

  }

  private sendForgotPasswordApi(payload: RequestNewForgotPasswordDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.accessManagementNestApi.newforgotPassword(payload)
            .pipe(
              map(param => param)
            )
        } else {
          return this.accessmanagementApi.NewForgotPassword(payload.companyCode, payload.emailUser)
            .pipe(
              map(param => param.result)
            )
        }
      })
  }

  public newCheckCode(companyCode: string, codenum: any, datereq: any, userId: string): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.accessManagementNestApi.newCheckCode({
            cocode: btoa(companyCode),
            code: codenum,
            datreq: datereq,
            userId: userId
          }).pipe(
            map(param => {
              return param.data;
            })
          )
        } else {
          return this.accessmanagementApi.NewCheckCode(btoa(companyCode), codenum, datereq, userId).pipe(
            map(param => {
              if (param.result == true) {
                return param.result;
              } else {
                throw Observable.throw({ message: param.result.message, status: param.result.status, customError: true });
              }
            })
          )
        }
      })
  }

  public submitNewPassword(payload: any): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.authApi.resetPassword({
            cocode: btoa(payload.companyCode),
            code: payload.codenum,
            datreq: payload.datereq,
            newPassword: btoa(btoa(payload.newpass).split("").reverse().join("")),
            confirmPassword: btoa(btoa(payload.newpass2).split("").reverse().join("")),
            userId: payload.userId,
            isEncrypted: true
          })
        } else {
          return this.accessmanagementApi.NewResetPassword(
            btoa(payload.companyCode), payload.codenum, payload.datereq, payload.newpass, payload.newpass2
          )
        }
      })
  }

  public async companyCodeProcess(payload: RequestNewForgotPasswordDto): Promise<any> {
    const temp_ist_split = payload.companyCode.trim().split('-');
    if (!isNaN(parseInt(temp_ist_split[0]))) {
      temp_ist_split[0] = 'sfgo' + temp_ist_split[0];
    }
    if (temp_ist_split.length == 1) {
      payload.companyCode = temp_ist_split[0];
    } else {
      payload.companyCode = temp_ist_split.join('-');
    }
    return payload;
  }

  public async loadSaas(companyCode: string): Promise<any> {
    this.auth.clear();
    this.menuRepository.clear();
    this.fcmServiceProvider.deleteUser();
    await this.initializeBloc.clearStorage();
    let saas = await this.authenticationBloc.saas(companyCode).toPromise();
    await this.authenticationBloc.saasProcess(saas.json());
    return true;
  }

  public async submitForgotPassword(payload: RequestNewForgotPasswordDto): Promise<any> {
    try {
      await this.loadSaas(payload.companyCode);
      const forgot = await this.sendForgotPasswordApi(payload).toPromise();
      return forgot;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw this.handleMsgError(error);
      } else {
        throw this.handleMsgErrorL(error);
      }
    }
  }

  public async sendNewForgotPassword(payload: any): Promise<any> {
    try {
      const forgot = await this.submitNewPassword(payload).toPromise();
      return forgot;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw this.handleMsgError(error);
      } else {
        throw this.handleMsgErrorL(error);
      }
    }
  }

  handleMsgError(error: any) {
    let msg = 'Company code or Email might be Invalid or not Registered';
    if (error instanceof HttpErrorResponse) {
      if (error.status < 500) {
        msg = error.error.message;
        if (error.error.message == 'Email Not Sent') {
          msg = 'Email Not Sent';
        } else if (error.error.message == "User inactive") {
          msg = 'Sorry,Your Account is Inactive, Please Contact Your HR Company to Make Your Account be Active';
        } else if (error.error.message == "The email is ambiguous, please using username instead") {
          msg = "The email is ambiguous, please using username instead";
        } else if (error.error.message == 'Company reg not found') {
          msg = "Company register not found"
        }
      }
      else {
        msg = Messages.SERVER_ERROR;
      }
    }
    else if (error instanceof TimeoutError) {
      msg = error.message
    }
    return msg;
  }

  handleMsgErrorL(error: any) {
    let msg = 'Company code or Email might be Invalid or not Registered';
    if (error.message == 'Email Not Sent') {
      msg = 'Email Not Sent';
    } else if (error.message == "User Is Blocked") {
      msg = 'Sorry,Your Account is Blocked, Please Contact Your HR Company to Make Your Account be Active';
    } else if (error.message == "User Is Inactive") {
      msg = 'Sorry,Your Account is Inactive, Please Contact Your HR Company to Make Your Account be Active';
    } else if (error.message == "The email is ambiguous, please using username instead") {
      msg = "The email is ambiguous, please using username instead";
    } else if (error.message == "Wrong Confirmation Code") {
      msg = "Wrong Confirmation Code";
    }
    return msg;
  }

}



// WEBPACK FOOTER //
// ./src/bloc/feature/forgot-password/forgot-password.bloc.ts